import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import { PatientLayout } from "./components/PatientLayout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Patients from "./pages/Patients";
import Records from "./pages/Records";
import ClinicalRecord from "./pages/ClinicalRecord";
import RescheduleQueue from "./pages/RescheduleQueue";
import AvailabilityBlocks from "./pages/AvailabilityBlocks";
import Reports from "./pages/Reports";
import Users from "./pages/Users";
import Settings from "./pages/Settings";
import PublicBooking from "./pages/PublicBooking";
import PatientPortal from "./pages/PatientPortal";
import PatientDashboard from "./pages/patient/PatientDashboard";
import PatientAppointmentsList from "./pages/patient/PatientAppointmentsList";
import PatientAppointmentShow from "./pages/patient/PatientAppointmentShow";
import PatientProfile from "./pages/patient/PatientProfile";

const PrivateRoute = ({ children }) => {
  const { user, loading, isPatient } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#335C67] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#5D5C61]">Cargando...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirigir usuarios PACIENTE a su ruta específica
  if (isPatient) {
    return <Navigate to="/mi-perfil" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

const PatientLayoutRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#335C67] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#5D5C61]">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'PACIENTE') {
    return <Navigate to="/" replace />;
  }

  return <PatientLayout>{children}</PatientLayout>;
};

const AdminRoute = ({ children }) => {
  const { user, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#335C67] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#5D5C61]">Cargando...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  
  return (
    <Routes>
      {/* PUBLIC ROUTES - No authentication required */}
      <Route path="/agendar" element={<PublicBooking />} />
      <Route path="/portal" element={<PatientPortal />} />
      
      {/* AUTH ROUTES */}
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      
      {/* PRIVATE ROUTES - Require authentication (ADMIN/ASISTENTE) */}
      <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/pacientes" element={<PrivateRoute><Patients /></PrivateRoute>} />
      <Route path="/expedientes" element={<PrivateRoute><Records /></PrivateRoute>} />
      <Route path="/expedientes/:patientId" element={<PrivateRoute><ClinicalRecord /></PrivateRoute>} />
      <Route path="/reagendar" element={<PrivateRoute><RescheduleQueue /></PrivateRoute>} />
      <Route path="/bloqueos" element={<PrivateRoute><AvailabilityBlocks /></PrivateRoute>} />
      <Route path="/reportes" element={<PrivateRoute><Reports /></PrivateRoute>} />
      
      {/* ADMIN ROUTES */}
      <Route path="/usuarios" element={<AdminRoute><Users /></AdminRoute>} />
      <Route path="/configuracion" element={<AdminRoute><Settings /></AdminRoute>} />
      
      {/* PATIENT ROUTES - Portal del paciente (layout y vistas propias) */}
      <Route path="/mi-perfil" element={<PatientLayoutRoute><PatientDashboard /></PatientLayoutRoute>} />
      <Route path="/mis-citas" element={<PatientLayoutRoute><PatientAppointmentsList /></PatientLayoutRoute>} />
      <Route path="/mis-citas/:id" element={<PatientLayoutRoute><PatientAppointmentShow /></PatientLayoutRoute>} />
      <Route path="/mi-cuenta" element={<PatientLayoutRoute><PatientProfile /></PatientLayoutRoute>} />

      {/* FALLBACK */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster 
          position="top-right"
          richColors
          closeButton
          duration={3000}
          toastOptions={{
            style: {
              fontFamily: 'Public Sans, sans-serif',
            },
          }}
        />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
